import { useEffect, useState } from "react";
import { Link, useNavigate, useRevalidator, useRouteLoaderData, useSubmit } from "react-router-dom";
import CurrentMaterialCost from "src/components/common/currentMaterialCost";
import FileSystem from "src/components/common/fileSystem";
import { useAuthStore, User } from "src/components/main/authStore";
import {
    arrayReducer,
    myAxios
} from "src/components/common/functions";
import { buildProjectName } from "./actions_loaders";
import { Project } from "./types";
import UserInfo from "../common/userInfo";

type ReportsItem = {
    reportId: string;
    reportTechName: string;
    reportTechId: string;
    startDate: string;
    endDate: string;
    pdfId: string;
    status: string;
};

function safeParse<T>(value: string, fallback: T): T {
    if (!value) return fallback;
    try {
        return JSON.parse(value) as T;
    } catch {
        return fallback;
    }
}

export default function ProjectDetail() {
    const project = useRouteLoaderData<Project>("project")!;
    const [projectItem, setProjectItem] = useState<Project>(project);
    const [reports, setReports] = useState<ReportsItem[]>([]);
    const [projectDetailRenderSwitch] = useState(false);
    const [potholing, setPotholing] = useState<{ pothole: string | number; manhole: string | number; electric: string | number }>({
        pothole: "Loading",
        manhole: "Loading",
        electric: "Loading",
    });
    const [sc, setSc] = useState<{ scanningCoring: string | number; scanning: string | number; coring: string | number }>({
        scanningCoring: "Loading",
        scanning: "Loading",
        coring: "Loading",
    });
    const [locating, setLocating] = useState<Record<string, string | number>>({
        "White (ft)": "Loading",
        "Pink (ft)": "Loading",
        "Red (ft)": "Loading",
        "Yellow (ft)": "Loading",
        "Orange (ft)": "Loading",
        "Blue (ft)": "Loading",
        "Purple (ft)": "Loading",
        "Green (ft)": "Loading",
        "Total (ft)": "Loading",
    });
    const user = useAuthStore(state => state.user) as User;
    const navigate = useNavigate();
    const submit = useSubmit();
    const { revalidate } = useRevalidator();

    useEffect(() => {
        setProjectItem(project);
    }, [project]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        const getData = async () => {
            const formData = new FormData();
            formData.append("id", projectItem.id);
            if (projectItem.pipeline === "GPR Concrete Scanning and Coring Project") {
                const resDataSheets = await myAxios({
                    fileName: "projects/readProjectSC.php",
                    formData,
                    signal: controller.signal,
                });
                if (!isMounted || !resDataSheets) return;
                const dataSheets = Array.isArray(resDataSheets.data) ? resDataSheets.data : [];
                if (dataSheets.length) {
                    setSc({
                        scanningCoring: dataSheets.filter(d => d.laborCategory === "GPR & Concrete Scaning & Coring").length,
                        scanning: dataSheets.filter(d => d.laborCategory === "GPR & Concrete Scanning").length,
                        coring: dataSheets.filter(d => d.laborCategory === "GPR & Concrete Coring").length,
                    });
                } else {
                    setSc({ scanningCoring: 0, scanning: 0, coring: 0 });
                }
            } else if (["Potholing Project", "Locating Project"].includes(projectItem.pipeline)) {
                const resDataSheets = await myAxios({
                    fileName: "projects/readProjectDataSheets.php",
                    formData,
                    signal: controller.signal,
                });
                if (!isMounted || !resDataSheets) return;
                const dataSheets = Array.isArray(resDataSheets.data) ? resDataSheets.data : [];
                const forms = dataSheets
                    .map(d => safeParse(d.forms, [] as any[]))
                    .reduce(arrayReducer, []);
                const defaultLocating = {
                    "White (ft)": "0", 
                    "Pink (ft)": "0", 
                    "Red (ft)": "0", 
                    "Yellow (ft)": "0", 
                    "Orange (ft)": "0", 
                    "Blue (ft)": "0", 
                    "Purple (ft)": "0", 
                    "Green (ft)": "0", 
                    "Total (ft)": "0", 
                };
                const reduceFunc = (left: Record<string, string>, right: Record<string, string>) => {
                    const next: Record<string, string> = {};
                    Object.keys(defaultLocating).forEach(key => {
                        next[key] = String(Number(left[key] || "0") + Number(right[key] || "0"));
                    });
                    next["Total (ft)"] = String(
                        Object.values(next).reduce((sum, value) => sum + Number(value || 0), 0)
                    );
                    return next;
                };
                const currentLocating = forms
                    .filter(form => form.form === "Locating Form")
                    .map(form => safeParse(form.content || "{}", {} as Record<string, string>))
                    .reduce((left, right) => reduceFunc(left, right), defaultLocating);
                setLocating(currentLocating);
                if (dataSheets.length) {
                    setPotholing({
                        pothole: forms.filter(form => form.form === "Potholing Form").length,
                        manhole: forms.filter(form => form.form === "Manhole Form").length,
                        electric: forms.filter(form => form.form === "Electric Pothole Form").length,
                    });
                } else {
                    setPotholing({ pothole: 0, manhole: 0, electric: 0 });
                }
            }
            const resReports = await myAxios({
                fileName: "projects/readReports.php",
                formData,
                signal: controller.signal,
            });
            if (!isMounted || !resReports) return;
            setReports(Array.isArray(resReports.data) ? resReports.data : []);
        };

        getData();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [projectItem.id, projectItem.pipeline, projectDetailRenderSwitch]);

    const cctv = safeParse(projectItem.cctv, null as null | Record<string, any>);
    const projectName = buildProjectName(projectItem);
    const setProjectItems = (updater:Function) => {
        setProjectItem(currentProject => {
            const nextItems = typeof updater === "function" ? updater([currentProject]) : updater;
            return Array.isArray(nextItems) ? (nextItems[0] ?? currentProject) : currentProject;
        });
    };

    const deleteProject = () => {
        if (!window.confirm(`Are you sure to delete ${projectName || "this project"}?`)) return;
        const formData = new FormData();
        formData.append("id", projectItem.id);
        submit(formData, {
            method: "post",
            action: `/Projects/${projectItem.id}/Delete`,
        });
    };
    const handleViewReportPdf = async (reportId: string) => {
        const formData = new FormData();
        formData.append("id", reportId);
        const res = await myAxios({ fileName: "reports/getReportPDF.php", formData });
        if (!res || !res.data) return;
        window.open(String(res.data), "_blank", "noopener,noreferrer");
    };

    return (
        <div className="pt-3">
            <div className="cssSeperateWhenWide ps-3">
                <div className="flex-grow-1 maxWidth-50 me-3">
                    <table className="table table-fixed">
                        <tbody className="noTableTopBorder">
                            <tr>
                                <th>Project Number:</th>
                                <td>{projectItem.projectNumber}</td>
                            </tr>
                            <tr>
                                <th>Organization Name:</th>
                                <td>
                                    {projectItem.organizationId ? (
                                        <Link to={`/Organizations/${projectItem.organizationId}`}>
                                            {projectItem.organizationName}
                                        </Link>
                                    ) : null}
                                </td>
                            </tr>
                            <tr>
                                <th>Client Project Number:</th>
                                <td>{projectItem.clientProjectNumber}</td>
                            </tr>
                            <tr>
                                <th>Report Needed:</th>
                                <td>{projectItem.reportNeeded}</td>
                            </tr>
                            {projectItem.usaTicketNumber ? (
                                <tr>
                                    <th>USA Ticket Number:</th>
                                    <td>{projectItem.usaTicketNumber}</td>
                                </tr>
                            ) : null}
                            {projectItem.usaTicketDate ? (
                                <tr>
                                    <th>USA Ticket Date:</th>
                                    <td>{projectItem.usaTicketDate}</td>
                                </tr>
                            ) : null}
                            <tr>
                                <th>Job Site Location:</th>
                                <td>{projectItem.location}</td>
                            </tr>
                            <tr>
                                <th>Nearest Medical Facility:</th>
                                <td>{projectItem.nearestMedicalFacility}</td>
                            </tr>
                            <tr>
                                <th>Opportunity Name:</th>
                                <td>
                                    {projectItem.opportunityId ? (
                                        <Link to={`/Opportunities/${projectItem.opportunityId}`}>
                                            {projectItem.opportunityName}
                                        </Link>
                                    ) : null}
                                </td>
                            </tr>
                            <tr>
                                <th>Proposal:</th>
                                <td>
                                    {projectItem.proposalId ? (
                                        <Link to={`/Proposals/${projectItem.proposalId}`}>
                                            {projectItem.proposalNumber}
                                        </Link>
                                    ) : null}
                                </td>
                            </tr>
                            {projectItem.contacts.length > 0 ?
                            <tr>
                                <th>Contacts:</th>
                                <td className="position-relative">
                                    {projectItem.contacts.slice(0, 5).map(contact => (
                                        <li key={contact.value}>
                                            <Link to={`/Contacts/${String(contact.value)}`}>
                                                {contact.label}
                                            </Link>
                                        </li>
                                    ))}
                                    {projectItem.contacts.length > 5? 
                                    <li><Link to = {`/Contacts?projectId=${projectItem.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                            :null}
                            <tr>
                                <th>Client PO:</th>
                                <td>{projectItem.clientPONumber}</td>
                            </tr>
                            <tr>
                                <th>Region:</th>
                                <td>{projectItem.region}</td>
                            </tr>
                            {projectItem.projectManagerName && projectItem.projectManagerId?
                            <tr>
                                <th>Project Manager:</th>
                                <td><UserInfo name = {projectItem.projectManagerName} id = {projectItem.projectManagerId}/></td>
                            </tr>
                            :null}
                            <tr>
                                <th>Creator:</th>
                                <td>
                                    <span><UserInfo name = {projectItem.creatorName} id = {projectItem.creatorId}/></span>
                                    <span className="ms-3">{projectItem.projectCreationDate}</span>
                                </td>
                            </tr>
                            {projectItem.projectUpdaterId? (
                                <tr>
                                    <th>Updater:</th>
                                    <td>
                                        <span><UserInfo name = {projectItem.updaterName} id = {projectItem.projectUpdaterId}/></span>
                                        <span className="ms-3">{projectItem.projectDateUpdated}</span>
                                    </td>
                                </tr>
                            ) : null}
                            {projectItem.statusChangerId ? (
                                <tr>
                                    <th>Stage Changer:</th>
                                    <td>
                                        <span><UserInfo name = {projectItem.statusChangerName} id = {projectItem.statusChangerId}/></span>
                                        <span className="ms-3">{projectItem.statusChangeDateTime}</span>
                                    </td>
                                </tr>
                            ) : null}
                            <tr>
                                <th>Description:</th>
                                <td>
                                    <div dangerouslySetInnerHTML={{ __html: projectItem.description }} />
                                </td>
                            </tr>
                            <tr>
                                <th>Notes:</th>
                                <td>
                                    <div dangerouslySetInnerHTML={{ __html: projectItem.notes }} />
                                </td>
                            </tr>
                            <tr>
                                <th>Accurate Time On Job Tag:</th>
                                <td>{projectItem.accurateTime === "yes" ? "Yes" : "No"}</td>
                            </tr>
                            <tr>
                                <th>Client Signature Required:</th>
                                <td>{projectItem.clientSignatureRequired === "yes" ? "Yes" : "No"}</td>
                            </tr>
                            <tr>
                                <th>Send To Client:</th>
                                <td>{projectItem.sendToClient === "yes" ? "Yes" : "No"}</td>
                            </tr>
                        </tbody>
                    </table>
                    <table className="table table-fixed">
                        <tbody className="noTableTopBorder">
                            <tr><th colSpan={2}  className="text-center">Project Specific Data ({projectItem.pipeline})</th></tr>
                            {projectItem.pipeline === "Potholing Project" ? (
                                <>
                                    <tr>
                                        <th>Number of Potholes:</th>
                                        <td>{potholing.pothole}</td>
                                    </tr>
                                    <tr>
                                        <th>Number of Manholes:</th>
                                        <td>{potholing.manhole}</td>
                                    </tr>
                                    <tr>
                                        <th>Number of Electric Potholes:</th>
                                        <td>{potholing.electric}</td>
                                    </tr>
                                </>
                            ) : null}
                            {projectItem.pipeline === "GPR Concrete Scanning and Coring Project" ? (
                                <>
                                    <tr>
                                        <th>Number of Scanning and Coring:</th>
                                        <td>{sc.scanningCoring}</td>
                                    </tr>
                                    <tr>
                                        <th>Number of Coring:</th>
                                        <td>{sc.coring}</td>
                                    </tr>
                                    <tr>
                                        <th>Number of Scanning:</th>
                                        <td>{sc.scanning}</td>
                                    </tr>
                                </>
                            ) : null}
                            {cctv ? (
                                <>
                                    <tr>
                                        <th>Completed Linear Footage:</th>
                                        <td>{cctv.linear}</td>
                                    </tr>
                                    <tr>
                                        <th>Completed Lateral Footage:</th>
                                        <td>{cctv.lateral}</td>
                                    </tr>
                                    <tr>
                                        <th>Video Required:</th>
                                        <td>{cctv.videoReq ? "Yes" : "No"}</td>
                                    </tr>
                                    {cctv.videoReq ? (
                                        <tr>
                                            <th>Video Captured:</th>
                                            <td>{cctv.videoCap ? "Yes" : "No"}</td>
                                        </tr>
                                    ) : null}
                                </>
                            ) : null}
                            {projectItem.pipeline === "Locating Project"
                                ? Object.entries(locating).map(([label, value]) => (
                                      <tr key={label}>
                                          <th>{label}:</th>
                                          <td>{value}</td>
                                      </tr>
                                  ))
                                : null}
                        </tbody>
                    </table>
                </div>
                <div className="flex-grow-1 maxWidth-50 me-3">
                    <table className="table table-fixed">
                        <tbody className="noTableTopBorder">
                            <tr>
                                <th>Estimate Budget:</th>
                                <td>{projectItem.budget}</td>
                            </tr>
     
                            <tr>
                                <th>Billing Type:</th>
                                <td>{projectItem.billingType}</td>
                            </tr>
                            {projectItem.billingType === "Day Rate" ? (
                                <tr>
                                    <th>Estimate Days:</th>
                                    <td>{projectItem.days}</td>
                                </tr>
                            ) : null}
                            {projectItem.billingType === "T & M" ? (
                                <>
                                    <tr>
                                        <th>Estimate Labor Hours:</th>
                                    <td>{projectItem.laborHours}</td>
                                    </tr>
                                    <tr>
                                        <th>Estimate Material Cost:</th>
                                    <td>{projectItem.materialCost}</td>
                                    </tr>
                                </>
                            ) : null}
                        </tbody>
                    </table>
                    {projectItem.budgets.length && projectItem.budgets[0]?.amount ?
                    <table className="table table-fixed">
                        <tbody className="noTableTopBorder">
                            <tr>
                                <th colSpan={3} className="text-center">
                                    Budget Scope
                                </th>
                            </tr>
                            <tr>
                                <th>Amount ($)</th>
                                <th>Start</th>
                                <th>End</th>
                            </tr>
                            {projectItem.budgets.map((budget, index) => (
                                <tr key={index}>
                                    <td>{budget.amount}</td>
                                    <td>{index === 0 ? budget.start : projectItem.budgets[index - 1]?.end}</td>
                                    <td>{budget.end}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    :null}
                    <div className="mt-3">
                    <CurrentMaterialCost
                        id={projectItem.id}
                        projectName={projectName}
                        className="mt-2"
                        uid = {null}
                    />
                    </div>
                    <div className="mt-3">
                    <FileSystem
                        item={projectItem}
                        fileSystem={[{
                            title: "Files",
                            folderIdLabel: "folderId",
                            folderKey: "projectFiles"
                        }]}
                        setItems={setProjectItems}
                        sqlTable="projects"
                        itemLabel="project"
                        idLabel="id"
                        filesKey="projectFiles"
                        singleFolderId="folderId"
                        readOnly={user?.projects !== "edit"}
                        onFilesChanged={revalidate}
                    />
                    </div>
                    {projectItem.reportNeeded ? (
                            <div className="mt-3">
                                <h4>
                                    Reports Status
                                    <span className="ms-2 badge badge-primary">{reports.length}</span>
                                </h4>
                                {reports.map(report => (
                                    <li key={report.reportId} className="my-2">
                                        <span><UserInfo name={report.reportTechName} id={report.reportTechId}/></span>
                                        <span className="ms-2">
                                            {report.startDate} ~ {report.endDate}
                                        </span>
                                        {report.pdfId ? (
                                            <button
                                                type="button"
                                                className="mx-2 btn btn-primary"
                                                onClick={() => handleViewReportPdf(report.reportId)}
                                            >
                                                View
                                            </button>
                                        ) : null}
                                        <Link className="mx-2 btn btn-info" to={`/Reports/${report.reportId}`}>
                                            Detail
                                        </Link>
                                        <span className="ms-2">{report.status}</span>
                                    </li>
                                ))}
                            </div>
                        ) : null}
                </div>
            </div>
            <div className="my-3">
                {user?.projects === "edit" ? (
                    <div className="d-flex gap-2">
                        <Link className="btn btn-success" to={`/Projects/${projectItem.id}/Edit`}>
                            Edit
                        </Link>
                        <button type="button" className="btn btn-danger" onClick={deleteProject}>
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary ms-auto"
                            onClick={() => navigate("/Projects")}
                        >
                            Back
                        </button>
                    </div>
                ) : (
                    <div className="d-flex">
                        <button
                            type="button"
                            className="btn btn-outline-secondary ms-auto"
                            onClick={() => navigate("/Projects")}
                        >
                            Back
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
